import type {
  CalcResult,
  Category,
  EffectiveKaten,
  GrowthJudgement,
  PlanInputs,
  WageJudgement,
} from '../types/core';

const EPS = 0.01;
// IEEE754 演算誤差の吸収用（0.035 * 100 − 0.01 ≠ 3.49 等への対処）
const FLOAT_EPS = 1e-9;

export function requiredRate(category: Category): number {
  switch (category) {
    case 'A_no':
      return 0.01; // 加点希望なしでも最低1%の成長を計画値の既定とする（加点判定には影響しない）
    case 'A_yes':
      return 0.03;
    case 'B':
      return 0.035;
  }
}

// 推奨計画レンジ（数値に曖昧さを持たせるため、年ごとに異なる成長率で自動配分する）
// 下限は要件最低水準（A_yes:3.0%, B:3.5%）に余裕を持たせ、上限は実現可能な範囲に抑える。
export function recommendedRange(category: Category): { lower: number; upper: number } {
  switch (category) {
    case 'A_no':
      return { lower: 0.01, upper: 0.015 };
    case 'A_yes':
      return { lower: 0.031, upper: 0.032 };
    case 'B':
      return { lower: 0.035, upper: 0.036 };
  }
}

// 決定論的な年ごとの成長率（年1=lower, 年2=midpoint, 年3=upper）
// PatternComparison など、再描画ごとに値がブレてはいけない箇所で使用。
export function yearlyRates(category: Category): [number, number, number] {
  const { lower, upper } = recommendedRange(category);
  const mid = (lower + upper) / 2;
  return [lower, mid, upper];
}

// レンジ内ランダムな年ごとの成長率（自動算出ボタン押下時に使用）
// 数値の曖昧さを保つため、押すたびに異なる値を生成する。
export function randomYearlyRates(category: Category): [number, number, number] {
  const { lower, upper } = recommendedRange(category);
  const rand = () => lower + Math.random() * (upper - lower);
  return [rand(), rand(), rand()];
}

export function calcCagr(p0: number, pN: number, years: number): number {
  if (!isFinite(p0) || !isFinite(pN) || p0 <= 0) return NaN;
  return (Math.pow(pN / p0, 1 / years) - 1) * 100;
}

export function autoFillPlan(
  category: Category,
  s0: number,
  e0: number,
  e1: number,
  e2: number,
  e3: number,
  rates?: [number, number, number]
): { salary1: number; salary2: number; salary3: number } {
  if (e0 <= 0) {
    return { salary1: s0, salary2: s0, salary3: s0 };
  }
  // 年ごとに異なる成長率を適用（呼出側でランダム or 決定論を選択可能）
  const [r1, r2, r3] = rates ?? yearlyRates(category);
  const per0 = s0 / e0;
  // 1人当たり給与は切り上げで丸める（前年水準を割らないようにするため）
  return {
    salary1: Math.ceil(per0 * (1 + r1)) * e1,
    salary2: Math.ceil(per0 * Math.pow(1 + r2, 2)) * e2,
    salary3: Math.ceil(per0 * Math.pow(1 + r3, 3)) * e3,
  };
}

export function judgeWage(siteMin: number, regionMin: number): WageJudgement {
  const diff = siteMin - regionMin;
  if (diff < 0) return { kind: 'unable', diff };
  if (diff < 30) return { kind: 'no_addon', diff };
  if (diff < 50) return { kind: 'plus_30', diff };
  return { kind: 'plus_50', diff };
}

export function judgeGrowth(category: Category, cagr3: number): GrowthJudgement {
  if (category === 'A_no') return 'NEUTRAL';
  if (!isFinite(cagr3)) return 'NG';
  const required = requiredRate(category) * 100;
  return cagr3 >= required - EPS - FLOAT_EPS ? 'OK' : 'NG';
}

function judgeYear(category: Category, cagrPct: number): GrowthJudgement {
  if (category === 'A_no') return 'NEUTRAL';
  if (!isFinite(cagrPct)) return 'NG';
  return cagrPct >= requiredRate(category) * 100 - EPS - FLOAT_EPS ? 'OK' : 'NG';
}

export function effectiveKaten(
  category: Category,
  growth: GrowthJudgement,
  wage: WageJudgement
): EffectiveKaten {
  if (category === 'A_no') return 'なし（加点希望せず）';
  if (growth === 'NG') {
    return category === 'A_yes'
      ? '加点取消（成長率未達）'
      : '申請要件違反（返還リスク）';
  }
  switch (wage.kind) {
    case 'unable':
      return '申請不可（賃金違法）';
    case 'no_addon':
      return '加点なし（賃金差不足）';
    case 'plus_30':
      return '+30円加点取得';
    case 'plus_50':
      return '+50円加点取得（最高位）';
  }
}

export function calculate(input: PlanInputs): CalcResult {
  const {
    category,
    prevSalary: s0,
    prevEmployees: e0Raw,
    salary1: s1,
    salary2: s2,
    salary3: s3,
    emp1: e1Raw,
    emp2: e2Raw,
    emp3: e3Raw,
    siteMin,
    regionMin,
  } = input;

  const e0 = e0Raw > 0 ? e0Raw : 1;
  const e1 = e1Raw > 0 ? e1Raw : 1;
  const e2 = e2Raw > 0 ? e2Raw : 1;
  const e3 = e3Raw > 0 ? e3Raw : 1;

  const p0 = s0 / e0;
  const p1 = s1 / e1;
  const p2 = s2 / e2;
  const p3 = s3 / e3;

  const cagr1 = calcCagr(p0, p1, 1);
  const cagr2 = calcCagr(p0, p2, 2);
  const cagr3 = calcCagr(p0, p3, 3);

  const r = requiredRate(category);
  const targetPerEmp3 = p0 * Math.pow(1 + r, 3);
  const targetSalary3 = targetPerEmp3 * e3;

  const growthJudge = judgeGrowth(category, cagr3);
  const wageJudge = judgeWage(siteMin, regionMin);
  const effective = effectiveKaten(category, growthJudge, wageJudge);

  return {
    perEmp: [p0, p1, p2, p3],
    cagr1,
    cagr2,
    cagr3,
    requiredRate: r,
    targetPerEmp3,
    targetSalary3,
    growthJudge,
    wageJudge,
    effective,
    yearJudge: [
      judgeYear(category, cagr1),
      judgeYear(category, cagr2),
      judgeYear(category, cagr3),
    ],
  };
}

// 加点措置② 判定: 直近月の事業場内最低賃金 ≧ R7.7時点 + 63円
export function judgeMeasure2(siteMin: number, regionMinR7Jul: number | null): boolean | null {
  if (regionMinR7Jul === null) return null;
  return siteMin >= regionMinR7Jul + 63;
}
